import React, {
  Dispatch,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Form, Input, Button, Alert, Carousel, message } from "antd";
import loginPic from "images/mfaPic.jpg";
import logo from "images/ejarafav.jpeg";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuthQuery, useBaseQuery } from "hooks/query.hook";
import { apiRoutes } from "api/data/routes";
import { routes } from "data/routes";
import { AuthContext } from "contexts/auth.context";
import { useDispatch } from "react-redux";
import { login } from "reducers/auth/actions-creator";
import { useTranslation } from "react-i18next";
import { MfaTypeList } from "components/mfaTypeList";

const Mfa = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("login");
  const {
    data: loginData,
    error,
    isLoading,
    post: handleVerify,
  } = useAuthQuery(apiRoutes.AUTH_ADMIN_VERIFY_CODE);

  const {
    data: verifyMfaData,
    error: verifyMfaError,
    isLoading: isLoadingVerifyMfa,
    post: handleVerifyMfa,
  } = useAuthQuery(apiRoutes.AUTH_ADMIN_VERIFY_MFA);

  const { post } = useBaseQuery(`${apiRoutes.SET_LANGUAGE}/${i18n.language}`);
  const { isAuthenticated } = useContext(AuthContext);
  const dispatch: Dispatch<any> = useDispatch();
  const { state } = useLocation();

  const [type, setType] = useState<string>("");
  const sliderRef = useRef<any>(null);
  const table = ["multiple", "authenticator", "email", "sms"];

  useEffect(() => {
    if (!state?.loginReference) {
      navigate(routes.AUTH_LOGIN);
    }
    const { mfaData } = state;

    if (mfaData && mfaData.length === 1) {      
      setType(mfaData[0].type);
    } else {
      setType("multiple");
    }
  }, []);

  useEffect(() => {
    if (sliderRef.current && type) {
      const index = table.indexOf(type);
      if (index !== -1 && sliderRef.current !== index) {
        sliderRef.current.goTo(index);
      }
    }

    if (type === "email" || type === "sms") {
      handleVerifyMfa({ type, loginReference: state?.loginReference });
    }
  }, [type]);

  useEffect(() => {
    if (loginData?.token) {
      const {
        expires_in,
        refresh_token,
        token,
        refresh_token_expires_in,
        data,
      } = loginData;
      dispatch(
        login({
          payload: {
            expires_in,
            refresh_token,
            refresh_token_expires_in,
            token,
          },
          user: data,
        })
      );
      post({});
      navigate(routes.HOME);
    }
    // eslint-disable-next-line
  }, [loginData]);

  useEffect(() => {
    if (verifyMfaError) {
      message.error(verifyMfaError || t("somethingWentWrong"));
      sliderRef.current?.goTo(0);
    }
  }, [verifyMfaError]);

  if (isAuthenticated) {
    return <Navigate to={routes.HOME} state={{ from: location }} replace />;
  }

  return (
    <div className="login">
      <div className="login-img">
        <img src={loginPic} alt="lady holding phone" />
      </div>
      <div className="login-form-container">
        <div className="login-form-content">
          <img src={logo} alt="ejara logo" />
          <h3>{t("welcome")},</h3>
          <Carousel dots={false} ref={sliderRef}>
            <div>
              <h2>{t("mfaTypeMessage")}</h2>

              <MfaTypeList
                choose={setType}
                list={state?.mfaData.map((elt: any) => elt.type)}
              />
            </div>
            <div>
              <h2>{t("otpMessage")}</h2>
              <Form
                name="login-form"
                requiredMark={false}
                onFinish={(values) => {
                  handleVerify({
                    ...values,
                    loginReference: state?.loginReference,
                  });
                }}
                layout="vertical"
                className="w-full"
              >
                <Form.Item
                  name="code"
                  label={t("otpLabel")}
                  rules={[{ required: true, message: "" }]}
                >
                  <Input.OTP
                    variant="filled"
                    disabled={isLoading}
                    size="large"
                  />
                </Form.Item>
                <div className="btn-container">
                  {state?.mfaData?.length > 1 && (
                    <Button
                      loading={isLoadingVerifyMfa || isLoading}
                      block
                      size="large"
                      type="text"
                      onClick={() => sliderRef.current?.goTo(0)}
                    >
                      {t("goBackBtnLabel")}
                    </Button>
                  )}
                  <Button
                    className="primary"
                    loading={isLoadingVerifyMfa || isLoading}
                    block
                    size="large"
                    htmlType="submit"
                  >
                    {t("continueBtnLabel")}
                  </Button>
                </div>
                {error && (
                  <Alert
                    message={error}
                    type="error"
                    className="text-error mt-10 "
                    showIcon
                    closable
                  />
                )}
              </Form>
            </div>
            <div>
              <h2>{t("otpEmailMessage")}</h2>
              <Form
                name="login-form"
                requiredMark={false}
                onFinish={(values) => {
                  handleVerify({
                    ...values,
                    loginReference: state?.loginReference,
                    mfaReference: verifyMfaData?.data?.reference,
                  });
                }}
                layout="vertical"
                className="w-full"
              >
                <Form.Item
                  name="code"
                  label={t("otpLabel")}
                  rules={[{ required: true, message: "" }]}
                >
                  <Input.OTP
                    variant="filled"
                    disabled={isLoadingVerifyMfa || isLoading}
                    size="large"
                  />
                </Form.Item>
                <div className="btn-container">
                  {state?.mfaData?.length > 1 && (
                    <Button
                      loading={isLoadingVerifyMfa || isLoading}
                      block
                      size="large"
                      type="text"
                      onClick={() => sliderRef.current?.goTo(0)}
                    >
                      {t("goBackBtnLabel")}
                    </Button>
                  )}
                  <Button
                    className="primary"
                    loading={isLoadingVerifyMfa || isLoading}
                    block
                    size="large"
                    htmlType="submit"
                  >
                    {t("continueBtnLabel")}
                  </Button>
                </div>
                {error && (
                  <Alert
                    message={error}
                    type="error"
                    className="text-error mt-10 "
                    showIcon
                    closable
                  />
                )}
              </Form>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  );
};

export default Mfa;
